/**
 * src/hooks/useSession.ts
 * -------------------------
 * React hook for session lifecycle management.
 *
 * Fully implemented this will:
 *   - fetchSessions() — calls listSessions() API, updates session store.
 *   - createSession() — calls createSession() API with VITE_ORG_ID,
 *     prepends to store, sets as active, clears chat messages.
 *   - selectSession(id) — sets active session, loads message history.
 *   - deleteSession(id) — calls deleteSession() API, removes from store.
 *   - renameSession(id, title) — calls renameSession() API, updates store.
 *
 * Key dependencies:
 *   src/stores/session.ts, src/api/sessions.ts,
 *   src/hooks/useChat.ts (for loadHistory)
 */
import { useCallback } from 'react'
import { useSessionStore } from '@/stores/session'
import {
  listSessions,
  createSession as apiCreateSession,
  getSession as apiGetSession,
  deleteSession as apiDeleteSession,
  renameSession as apiRenameSession,
} from '@/api/sessions'
import { useChat } from './useChat'
import { getCurrentOrgId } from '@/lib/org'

export function useSession() {
  const sessions = useSessionStore((state) => state.sessions)
  const activeSessionId = useSessionStore((state) => state.activeSessionId)
  const isLoading = useSessionStore((state) => state.isLoading)
  const setLoading = useSessionStore((state) => state.setLoading)
  const loadSessions = useSessionStore((state) => state.loadSessions)
  const prependSession = useSessionStore((state) => state.prependSession)
  const setActiveSession = useSessionStore((state) => state.setActiveSession)
  const removeSession = useSessionStore((state) => state.removeSession)
  const renameSessionLocal = useSessionStore((state) => state.renameSession)
  const setAnonSessionId = useSessionStore((state) => state.setAnonSessionId)
  const { loadHistory } = useChat()

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    try {
      const sessions = await listSessions()
      loadSessions(sessions)
    } finally {
      setLoading(false)
    }
  }, [loadSessions, setLoading])

  const createNewSession = useCallback(async () => {
    const orgId = getCurrentOrgId()
    setLoading(true)
    try {
      const session = await apiCreateSession(orgId)
      prependSession(session)
      setActiveSession(session.id)
      // Save anon session ID for potential post-login merge
      if (session.isAnonymous) {
        setAnonSessionId(session.id)
      }
    } finally {
      setLoading(false)
    }
  }, [prependSession, setActiveSession, setAnonSessionId, setLoading])

  const restoreAnonymousSession = useCallback(async (): Promise<boolean> => {
    const id = useSessionStore.getState().anonSessionId
    if (!id) return false
    try {
      const session = await apiGetSession(id)
      if (session.orgId === getCurrentOrgId() && session.isAnonymous && session.status !== 'resolved') {
        prependSession(session)
        setActiveSession(session.id)
        return true
      }
      setAnonSessionId(null)
    } catch (error) {
      const status = (error as { response?: { status?: number } } | null)?.response?.status
      if (status !== 403 && status !== 404) throw error
      setAnonSessionId(null)
    }
    return false
  }, [prependSession, setActiveSession, setAnonSessionId])

  const selectSession = useCallback(
    async (id: string) => {
      setActiveSession(id)
      await loadHistory(id)
    },
    [loadHistory, setActiveSession],
  )

  const deleteSession = useCallback(
    async (id: string) => {
      removeSession(id)
      await apiDeleteSession(id)
    },
    [removeSession],
  )

  const renameSession = useCallback(
    async (id: string, title: string) => {
      renameSessionLocal(id, title)
      await apiRenameSession(id, title)
    },
    [renameSessionLocal],
  )

  return {
    sessions,
    activeSessionId,
    isLoading,
    fetchSessions,
    createNewSession,
    restoreAnonymousSession,
    selectSession,
    deleteSession,
    renameSession,
  }
}
